import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Award, 
  Plane, 
  ChevronDown,
  ChevronUp,
  FileCheck,
  Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHelicopterIcons } from '@/hooks/useHelicopterIcons';

interface JobQualificationBadgesProps {
  licenses?: string[];
  certifications?: string[];
  typeRatings?: string[];
  skills?: string[];
  maxShow?: number;
  variant?: 'default' | 'compact' | 'detailed';
  className?: string;
  showHelicopterIcons?: boolean;
}

const JobQualificationBadges: React.FC<JobQualificationBadgesProps> = ({
  licenses = [],
  certifications = [],
  typeRatings = [],
  skills = [],
  maxShow = 6,
  variant = 'default',
  className,
  showHelicopterIcons = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Use centralized hook for helicopter icons
  const { icons: helicopterIconMatches } = useHelicopterIcons(
    showHelicopterIcons ? typeRatings : []
  );

  // Find matching helicopter icon for a type rating
  const findHelicopterIcon = (rating: string): string | null => {
    const match = helicopterIconMatches.find(
      m => m.typeRating.toLowerCase() === rating.toLowerCase()
    );
    return match?.iconUrl || null;
  };

  // Create unified list with types - TYPE RATINGS FIRST (most important for aviation jobs)
  const allQualifications = [
    // Type ratings first - top requirement for aviation positions
    ...typeRatings.map(t => ({ type: 'type_rating' as const, value: t, icon: Plane, heliIcon: findHelicopterIcon(t) })),
    // Then licenses
    ...licenses.map(l => ({ type: 'license' as const, value: l, icon: Award, heliIcon: null as string | null })),
    // Then certifications
    ...certifications.map(c => ({ type: 'certification' as const, value: c, icon: FileCheck, heliIcon: null as string | null })),
    // Then skills (limited)
    ...skills.slice(0, 3).map(s => ({ type: 'skill' as const, value: s, icon: Star, heliIcon: null as string | null })),
  ];

  if (allQualifications.length === 0) return null;

  const visibleItems = isExpanded ? allQualifications : allQualifications.slice(0, maxShow);
  const hiddenCount = allQualifications.length - maxShow;
  const showExpandButton = hiddenCount > 0;

  const getQualificationStyle = (type: string) => {
    switch (type) {
      case 'license':
        return {
          bg: 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/40 dark:to-green-900/30',
          border: 'border-emerald-200 dark:border-emerald-700/50',
          text: 'text-emerald-700 dark:text-emerald-300',
          iconColor: 'text-emerald-600 dark:text-emerald-400',
        };
      case 'certification':
        return {
          bg: 'bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-900/40 dark:to-sky-900/30',
          border: 'border-blue-200 dark:border-blue-700/50',
          text: 'text-blue-700 dark:text-blue-300',
          iconColor: 'text-blue-600 dark:text-blue-400',
        };
      case 'type_rating':
        return {
          bg: 'bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/40 dark:to-purple-900/30',
          border: 'border-violet-200 dark:border-violet-700/50',
          text: 'text-violet-700 dark:text-violet-300',
          iconColor: 'text-violet-600 dark:text-violet-400',
        };
      case 'skill':
        return {
          bg: 'bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800/50 dark:to-gray-800/40',
          border: 'border-slate-200 dark:border-slate-600/50',
          text: 'text-slate-700 dark:text-slate-300',
          iconColor: 'text-slate-500 dark:text-slate-400',
        };
      default:
        return {
          bg: 'bg-muted',
          border: 'border-border',
          text: 'text-foreground',
          iconColor: 'text-muted-foreground',
        };
    }
  };

  // Compact variant for cards
  if (variant === 'compact') {
    return (
      <div className={cn("flex flex-wrap gap-1", className)}>
        {visibleItems.slice(0, 4).map((qual, idx) => {
          const style = getQualificationStyle(qual.type);
          const Icon = qual.icon;
          // Shorten long names
          const shortName = qual.value.split('(')[0].trim().slice(0, 20);
          
          return (
            <Badge 
              key={idx}
              variant="outline"
              className={cn(
                "text-[10px] px-1.5 py-0.5 font-medium gap-1",
                style.bg, style.border, style.text
              )}
            >
              {qual.heliIcon ? (
                <img 
                  src={qual.heliIcon} 
                  alt="" 
                  className="w-4 h-4 object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <Icon className={cn("w-2.5 h-2.5", style.iconColor)} />
              )}
              {shortName}
            </Badge>
          );
        })}
        {allQualifications.length > 4 && (
          <Badge 
            variant="secondary" 
            className="text-[10px] px-1.5 py-0.5 bg-muted/80"
          >
            +{allQualifications.length - 4}
          </Badge>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn("space-y-2", className)}>
      {/* Qualification badges */}
      <div className="flex flex-wrap gap-1.5">
        {visibleItems.map((qual, idx) => {
          const style = getQualificationStyle(qual.type);
          const Icon = qual.icon;
          
          return (
            <Badge 
              key={idx}
              variant="outline"
              className={cn(
                "text-xs px-2 py-1 font-medium gap-1.5 shadow-sm",
                style.bg, style.border, style.text
              )}
            >
              {qual.heliIcon ? (
                <img 
                  src={qual.heliIcon} 
                  alt="" 
                  className="w-5 h-5 object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <Icon className={cn("w-3 h-3", style.iconColor)} />
              )}
              {qual.value.split('(')[0].trim()}
            </Badge>
          );
        })}
      </div>

      {/* Expand/collapse button */}
      {showExpandButton && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3 mr-1" />
              +{hiddenCount} more qualifications
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default JobQualificationBadges;
